import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  apiGetExtendedLeavePolicies,
  apiUpdateExtendedLeavePolicy,
  type ExtendedLeaveTenantSettings,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/hooks/use-toast";
import { BabyIcon, ShieldCheckIcon, CalendarDaysIcon, RotateCcwIcon, BellIcon, InfoIcon } from "lucide-react";

// ─── Source badge ─────────────────────────────────────────────────────────────

function SourceBadge({ useCountryDefaults }: { useCountryDefaults: boolean }) {
  const { t } = useTranslation();
  if (useCountryDefaults) {
    return (
      <Badge variant="outline" className="gap-1 text-xs border-blue-400/50 text-blue-400 bg-blue-400/10">
        <ShieldCheckIcon className="w-3 h-3" />
        {t("leave.extended.sourceCountryDefault", "Country Default")}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="gap-1 text-xs border-violet-400/50 text-violet-400 bg-violet-400/10">
      <ShieldCheckIcon className="w-3 h-3" />
      {t("leave.extended.sourceTenantOverride", "Custom Override")}
    </Badge>
  );
}

// ─── Leave type icon ──────────────────────────────────────────────────────────

function LeaveTypeIcon({ leaveType }: { leaveType: string }) {
  if (leaveType === "maternity") return <BabyIcon className="w-5 h-5 text-pink-400" />;
  if (leaveType === "paternity") return <BabyIcon className="w-5 h-5 text-blue-400" />;
  return <CalendarDaysIcon className="w-5 h-5 text-violet-400" />;
}

// ─── Single policy card ───────────────────────────────────────────────────────

interface PolicyCardProps {
  settings: ExtendedLeaveTenantSettings;
  onSave: (updated: ExtendedLeaveTenantSettings) => Promise<void>;
}

function PolicyCard({ settings: initial, onSave }: PolicyCardProps) {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<ExtendedLeaveTenantSettings>(initial);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const update = useCallback(<K extends keyof ExtendedLeaveTenantSettings>(
    key: K,
    value: ExtendedLeaveTenantSettings[K],
  ) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setDirty(true);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(settings);
      setDirty(false);
    } finally {
      setSaving(false);
    }
  };

  const leaveLabel = settings.leaveType === "maternity"
    ? t("leave.maternity.label", "Maternity Leave")
    : settings.leaveType === "paternity"
      ? t("leave.paternity.label", "Paternity Leave")
      : settings.leaveType.charAt(0).toUpperCase() + settings.leaveType.slice(1) + " Leave";

  const accentClass = settings.leaveType === "maternity"
    ? "border-pink-500/30 bg-pink-500/5"
    : settings.leaveType === "paternity"
      ? "border-blue-500/30 bg-blue-500/5"
      : "border-violet-500/30 bg-violet-500/5";

  return (
    <div className={`rounded-xl border p-5 space-y-5 transition-all ${accentClass} ${!settings.enabled ? "opacity-60" : ""}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-background/60 border border-border/50 flex items-center justify-center">
            <LeaveTypeIcon leaveType={settings.leaveType} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-foreground">{leaveLabel}</h3>
              <SourceBadge useCountryDefaults={settings.useCountryDefaults} />
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t("leave.extended.policyCardSubtitle", "Configure how this leave type behaves for your organisation")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Label htmlFor={`enabled-${settings.leaveType}`} className="text-sm text-muted-foreground">
            {t("common.enabled", "Enabled")}
          </Label>
          <Switch
            id={`enabled-${settings.leaveType}`}
            checked={settings.enabled}
            onCheckedChange={v => update("enabled", v)}
          />
        </div>
      </div>

      <Separator className="opacity-30" />

      {/* Duration */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <CalendarDaysIcon className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">{t("leave.extended.duration", "Duration")}</span>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              id={`country-defaults-${settings.leaveType}`}
              checked={settings.useCountryDefaults}
              onCheckedChange={v => update("useCountryDefaults", v)}
            />
            <Label htmlFor={`country-defaults-${settings.leaveType}`} className="text-sm">
              {t("leave.extended.useCountryDefaults", "Use country defaults")}
            </Label>
          </div>
          {!settings.useCountryDefaults && (
            <div className="space-y-1.5 ml-1">
              <Label className="text-xs text-muted-foreground">
                {t("leave.extended.customDurationDays", "Custom duration (calendar days)")}
              </Label>
              <Input
                id={`custom-duration-${settings.leaveType}`}
                type="number"
                min={1}
                max={730}
                value={settings.customDurationDays ?? ""}
                onChange={e => update("customDurationDays", e.target.value ? Number(e.target.value) : null)}
                placeholder={t("leave.extended.customDurationPlaceholder", "e.g. 112")}
                className="max-w-[160px]"
              />
            </div>
          )}
          {/* Manual override toggle */}
          <div className="flex items-center gap-3 mt-1">
            <Switch
              id={`override-${settings.leaveType}`}
              checked={settings.allowManualOverride}
              onCheckedChange={v => update("allowManualOverride", v)}
            />
            <Label htmlFor={`override-${settings.leaveType}`} className="text-sm">
              {t("leave.extended.allowManualOverride", "Allow HR to override end date")}
            </Label>
          </div>
        </div>

        <div className="space-y-3">
          {/* Return reminder */}
          <div className="flex items-center gap-2">
            <BellIcon className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">{t("leave.extended.returnReminder", "Return Reminder")}</span>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">
              {t("leave.extended.returnReminderDays", "Send reminder X days before return")}
            </Label>
            <Input
              id={`reminder-days-${settings.leaveType}`}
              type="number"
              min={1}
              max={90}
              value={settings.returnReminderDaysBefore}
              onChange={e => update("returnReminderDaysBefore", Number(e.target.value) || 14)}
              className="max-w-[120px]"
            />
          </div>
        </div>
      </div>

      <Separator className="opacity-30" />

      {/* Reactivation */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <RotateCcwIcon className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium">{t("leave.extended.reactivation", "Employee Reactivation")}</span>
        </div>
        <div className="flex items-start gap-3">
          <Switch
            id={`auto-reactivate-${settings.leaveType}`}
            checked={settings.autoReactivateEmployee}
            onCheckedChange={v => update("autoReactivateEmployee", v)}
          />
          <div>
            <Label htmlFor={`auto-reactivate-${settings.leaveType}`} className="text-sm">
              {t("leave.extended.autoReactivate", "Automatically reactivate on return date")}
            </Label>
            <p className="text-xs text-muted-foreground mt-0.5">
              {settings.autoReactivateEmployee
                ? t("leave.extended.autoReactivateHint", "Employee will be automatically restored to Active status on their return date.")
                : t("leave.extended.manualReactivateHint", "HR must manually reactivate the employee from the leave request detail view.")}
            </p>
          </div>
        </div>
      </div>

      {/* Save button */}
      {dirty && (
        <div className="flex justify-end pt-1">
          <Button
            id={`save-extended-policy-${settings.leaveType}`}
            onClick={handleSave}
            disabled={saving}
            size="sm"
            className="gap-2"
          >
            {saving ? t("common.saving", "Saving...") : t("common.saveChanges", "Save Changes")}
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Main Tab Component ───────────────────────────────────────────────────────

export function ExtendedLeavePolicyTab() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<ExtendedLeaveTenantSettings[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiGetExtendedLeavePolicies()
      .then(res => setSettings(res.settings))
      .catch(() => toast({ title: t("common.error", "Error"), description: t("leave.extended.loadError", "Failed to load extended leave policies"), variant: "destructive" }))
      .finally(() => setLoading(false));
  }, [t]);

  const handleSave = useCallback(async (updated: ExtendedLeaveTenantSettings) => {
    const { leaveType, ...rest } = updated;
    await apiUpdateExtendedLeavePolicy(leaveType, rest);
    setSettings(prev => prev.map(s => s.leaveType === leaveType ? updated : s));
    toast({ title: t("leave.extended.saveSuccess", "Policy updated"), description: t("leave.extended.saveSuccessDesc", "Extended leave settings have been saved.") });
  }, [t]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info banner */}
      <div className="flex items-start gap-3 p-4 rounded-lg border border-violet-500/20 bg-violet-500/5">
        <InfoIcon className="w-4 h-4 text-violet-400 mt-0.5 shrink-0" />
        <div className="text-sm text-muted-foreground space-y-1">
          <p className="font-medium text-foreground">{t("leave.extended.infoTitle", "Extended Leave Framework")}</p>
          <p>
            {t("leave.extended.infoDesc",
              "Extended leave types (Maternity, Paternity, etc.) include special features: automatic employee status tracking, limited access mode during leave, configurable duration from country defaults or tenant overrides, and return reminders for HR.")}
          </p>
        </div>
      </div>

      {/* Policy cards */}
      {settings.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-10">
          {t("leave.extended.noTypes", "No extended leave types configured. Add maternity or paternity leave types to get started.")}
        </p>
      ) : (
        <div className="space-y-4">
          {settings.map(s => (
            <PolicyCard key={s.leaveType} settings={s} onSave={handleSave} />
          ))}
        </div>
      )}
    </div>
  );
}
